/**
 * Onboarding interview copy — Ori, 20 Jul 2026. SHIP VERBATIM.
 *
 * Same node shape as `wsCopy.js`: { d } or { d, m }, read through `pick`.
 * Where Ori wrote no mobile variant the desktop string IS the mobile string —
 * do not paraphrase for space.
 *
 * The QUESTION TEXT is deliberately NOT here. It comes from
 * GET /brands/generate/interview/questions, because the same wording is keyed
 * into the ICP/keyword prompts server-side (`backend/interview.py`) and two
 * copies is how the prompt and the screen drift apart. What lives here is only
 * what the backend has no opinion about: placeholder, hint, probe, button label.
 *
 * ── WHY THE SKIP BUTTONS ARE GONE (read before restoring them) ───────────────
 *
 * The previous version of this file carried honesty condition 7: "Nothing in
 * this table claims that answering produces a better result. Keep it that way."
 * That was correct when written — we had no evidence answering changed anything,
 * so claiming it would have been a sales line dressed as a fact.
 *
 * It was reversed on 20 Jul 2026 because it became measurably false. Measured on
 * the real Navin Classes brand, same website, interview answered vs skipped —
 * the words the engine ends up searching with:
 *
 *   ANSWERED: ca, inter, adv, accounts, audit, chartered, accounting, commerce,
 *             foudnation, cma, foundation, cs, navin
 *   SKIPPED:  ca, cs, cma, navin, provides, offering, comprehensive, personal,
 *             students, succeed, exams, faculty
 *
 * Skipping loses 9 real terms of the trade and leaves bare initialisms — and our
 * own prompt warns that "ca" also reads as California and Canada. Separately
 * measured: phrases built from what the owner TYPES are ~85% usable ("ca inter",
 * "chartered accounting", "cma foundation"); phrases built from website prose are
 * ~25% usable ("classes provides", "offering live", "help students").
 *
 * So the screen may now say that answering helps, because it does. Restoring
 * condition 7 would make this screen dishonest in the other direction — it would
 * hide a real difference from the one person it affects.
 *
 * WHAT THAT DOES **NOT** LICENSE: we have measured the WORDS, not the LEADS. No
 * string here may promise better leads, more leads, better results, or any
 * number. The honest claim is about what we go looking for, and it stops there.
 *
 * HONESTY CONDITIONS (build requirements, not preferences):
 *
 *  1. Q1 (`who_buys`) and Q2 (`buyer_words`) have NO skip button and no skip
 *     affordance — no link, no "later", no dismissable question. They define no
 *     `noAnswer` string, and there is no fallback label, so a question this build
 *     does not recognise renders no escape button either. That is deliberate: a
 *     generic "Skip" is exactly how skip would come back.
 *  2. Q3 (`blind_spots`) has `noAnswer` — "Nothing to add". It is an ANSWER, not
 *     a refusal: for many owners "nothing" is the true answer to "anything I'd
 *     get wrong?". It is never labelled "Skip" and never styled as an exit.
 *  3. `probe` is ONE gentle re-ask, fired at most once per question, on a thin
 *     (one-word) answer. If they press Send again, whatever they gave is
 *     accepted — `probeAccept` says so on the same screen, so no one is trapped.
 *     Never probe twice. Never guilt. Never a red state: a thin answer is not an
 *     error, it is a person being brief.
 *  4. `probeEmpty` is for a genuinely empty box, where Send stays disabled. It is
 *     the only string that states these questions are required, and it states it
 *     plainly and once. No "you must", no warning colour.
 *  5. `ack` is the WHOLE response to an answer. No praise, no "great answer".
 *     Praise on the answer path is disappointment on the "Nothing to add" path,
 *     politely delivered.
 *  6. `done` / `doneEarly` branch on how far GENERATION got, never on how much
 *     the owner typed. If the closing line branches on their effort, they feel
 *     the branch.
 *  7. `doneEarly` vs `done`: never claim "nearly there" while the job is
 *     genuinely early. Chosen once, at the moment the interview resolves, off
 *     the real phase — see InterviewStep.
 *  8. `progressHeld` may ONLY render while `waiting_on_owner === true`. It is a
 *     claim that nothing is running, and it is true only then.
 *  9. Every "this helps" line describes what we go LOOKING for. None of them
 *     promises a result, a lead, or a number. See the block above.
 */

/** Per-question extras. Keyed by the backend's question ids. */
export const INTERVIEW_QUESTIONS = {
  who_buys: {
    // Q1 and Q2 are deliberately the SAME imaginary business. The contrast
    // between how the owner talks and how buyers talk is the teaching moment —
    // changing one example without the other destroys the whole point.
    //
    // `hint` is the single sub-line slot under a question (already wired to
    // aria-describedby). One line, never two stacked: the reason to answer and
    // the way to answer are the same sentence or they are noise.
    hint: { d: "How you describe it is what I go looking for. A line or two is plenty." },
    placeholder: { d: "Home bakery in Pune. Mostly mums ordering birthday cakes, 30s–40s." },
    probe: { d: "A little more if you can — what you sell, and who pays you for it." },
  },
  buyer_words: {
    // Instruction AND reason in one line. The instruction is the load-bearing
    // half: it tells them the MODE to answer in.
    hint: { d: "Their words, not yours — even the messy way they'd put it. These shape what I search for." },
    placeholder: {
      d: "\"anyone know a good cake person in Baner\", \"need a birthday cake by Saturday help\", \"is it cheaper to just buy one\"",
    },
    probe: { d: "Two or three phrases if you can — half-typed ones count." },
  },
  blind_spots: {
    hint: { d: "I use this to rule things out. If there's nothing, that's a real answer." },
    placeholder: { d: "We don't do wedding orders. And \"cake studio\" is what the fancy places call themselves, not us." },
    // NOT a skip. Different key from the old `skip` on purpose, so the old
    // render path cannot quietly resurrect a skip button on Q1/Q2.
    noAnswer: { d: "Nothing to add" },
  },
};

export const INTERVIEW_COPY = {
  // Sets the deal up front: three questions, they're needed, and what you type
  // is the thing that changes. No "quick", no "just" — this asks for real work,
  // and pretending otherwise is how the third question feels like a bait.
  leadIn: {
    d: "Reading your site now — takes about a minute. Three questions while I work. What you type is what I go looking for, so they're worth the minute.",
    m: "Reading your site — about a minute. Three questions while I work. What you type is what I go looking for.",
  },
  send: { d: "Send" },
  ack: { d: "Got it." },

  // ── The probe ──────────────────────────────────────────────────────────────
  // Shown ONCE, under the box, after a one-word answer. The per-question `probe`
  // is the re-ask; this line is the release valve that sits beside it. Both are
  // on screen together, so nobody has to guess whether they're allowed through.
  probeAccept: { d: "Or send what you've got — I'll work with it." },
  // Empty box, Send disabled. The one place we say these are needed.
  probeEmpty: { d: "I do need this one — even a rough line in your own words." },

  // "what you told me goes in with it" — everyone reaching this line has
  // answered Q1 and Q2, so it is true of every owner who gets here, including
  // one who had nothing to add on Q3. Still a fact about the process, not a
  // compliment, and it still does not branch on effort.
  //
  // The previous string said "nearly there". It was written against the
  // pre-split pipeline where the interview raced generation; now Phase 2 WAITS
  // on the interview, so this moment is always ~20% by construction and
  // "nearly there" has no reachable state where it is true.
  done: {
    d: "That's everything from me. Now I work out your ideal customers and the words they type — what you told me goes in with it.",
    m: "That's everything from me. Working out your ideal customers and their buyer words now — what you told me goes in with it.",
  },
  doneEarly: { d: "That's everything from me. I'm still reading through your site — hang tight." },

  // ── The no-website path ────────────────────────────────────────────────────
  // Someone who pasted their brand content has just TOLD us they have no site.
  // Saying "reading your site" to them is a small, avoidable lie, and it is the
  // kind that makes an owner wonder whether the app has them confused with
  // somebody else. `GEN_STAGE_LINES.start` already has this bug in production
  // ("Opening up your website…" on the dump path); matching the house here would
  // have propagated it into a new surface rather than inheriting it.
  //
  // Derived from the JOB (website_url / content_dump on the status payload), not
  // from a component prop — so it is still correct after a refresh, when the
  // local dumpMode flag is gone but the job is not.
  leadInDump: {
    d: "Reading what you sent — takes about a minute. Three questions while I work. What you type is what I go looking for, so they're worth the minute.",
    m: "Reading what you sent — about a minute. Three questions while I work. What you type is what I go looking for.",
  },
  progressHeldDump: { d: "Read what you sent — waiting on you" },
  doneEarlyDump: { d: "That's everything from me. I'm still reading through what you sent — hang tight." },
  // The held-progress label. Maya's line, and it is a factual report: at this
  // point the site really has been read, and the only thing outstanding really
  // is the owner.
  progressHeld: { d: "Read your site — waiting on you" },
};

/**
 * Did this job come from pasted content rather than a website?
 *
 * Read off the JOB, never a component prop: after a refresh the local `dumpMode`
 * flag is gone but the job is still there, and the copy has to stay correct.
 *
 * Deliberately keyed on the ABSENCE of a website_url rather than the presence of
 * content_dump: the status payload may omit a long dump, but a job that never had
 * a URL is unambiguous. Falsy website_url and no dump means we know nothing yet —
 * treat that as the website path, which is the existing wording and the common case.
 */
export function isDumpJob(gen) {
  // A website_url settles it: that job crawled a site.
  if (!gen || gen.website_url) return false;
  // No URL and content in hand — this is the pasted-content path.
  return !!gen.content_dump;
}

/**
 * Pick the dump variant of a string when this job has no website.
 *
 * Takes `pick` as an argument rather than importing it: `wsCopy` owns that
 * helper, and importing it here would make the copy tables depend on each other
 * for no reason. Every call site already imports `pick`.
 */
export function pickSourceAware(pick, gen, siteCopy, dumpCopy, mobile) {
  return pick(isDumpJob(gen) ? dumpCopy : siteCopy, mobile);
}
